import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Bookmark } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const suggestName = (inv) => {
  const who = inv.customer?.company_name || inv.customer?.contact || "";
  const what = (inv.items?.[0]?.description || "Rechnung").replace(/\s*\([^)]*\)\s*$/, "");
  return [what, who].filter(Boolean).join(" – ").slice(0, 80);
};

export const SaveTemplateDialog = ({ inv, onClose }) => {
  const qc = useQueryClient();
  const [name, setName] = useState(() => suggestName(inv));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("Bitte einen Namen angeben.");
    setBusy(true);
    try {
      await api.post("/templates", {
        name: name.trim(),
        items: inv.items.map((i) => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price })),
        vat_rate: inv.vat_rate,
        kleinunternehmer: inv.kleinunternehmer,
        note: inv.note || "",
        reference_label: inv.reference_label || "",
      });
      qc.invalidateQueries({ queryKey: ["templates"] });
      toast.success(`Vorlage „${name.trim()}“ gespeichert`);
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 no-print" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="save-template-dialog">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-lg inline-flex items-center gap-2"><Bookmark className="h-5 w-5" /> Als Vorlage speichern</h3>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-slate-100" data-testid="close-template-dialog"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm text-slate-500">Positionen, Preise, MwSt und Rechnungstext werden übernommen – Empfänger und Daten nicht.</p>
        <label className="block">
          <span className="block text-xs font-medium text-slate-600 mb-1">Name der Vorlage</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoFocus
            data-testid="template-name-input"
            className="w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50">Abbrechen</button>
          <button onClick={save} disabled={busy} data-testid="save-template-button" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 rounded-lg text-sm disabled:opacity-60">
            {busy ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
};
